import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "../css/Ind-details.css";
import Navbar from "./Navbar";
import TitleBar from "./TitleBar";
import Footer from "./Footer";
import { fetchTransHistory } from "../Services/Api";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa";


const PaymentHistory = ({ title = "Payment History", image }) => {
  const [tableData, setTableData] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate=useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetchTransHistory();

        // console.log(response);


        if (response.status === "success" && Array.isArray(response.data)) {
          // Add serial numbers to each row
          const dataWithSerial = response.data.map((item, index) => ({
            ...item,
            serialNo: index + 1
          }));
          setTableData(dataWithSerial);
        } else {
          console.error("Invalid response format:", response);
          setTableData([]);
        }
      } catch (err) {
        console.error("Error fetching transaction details:", err);
        setTableData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const columns = [
    {
      name: "S.no",
      selector: (row) => row.serialNo,
      sortable: true,
      width: "80px"
    },
    {
      name: "Transaction ID",
      selector: (row) => row.transaction_id,
      sortable: true,
      cell: row => <div className="transaction-id-cell">{row.transaction_id}</div>
    },
    {
      name: "Date",
      selector: (row) => row.date,
      sortable: true,
      cell: row => <div className="date-cell">{new Date(row.date).toLocaleDateString()}</div>
    },
    {
      name: "No of Students",
      selector: (row) => row.no_of_users,
      sortable: true,
      cell: row => <div className="users-cell">{row.no_of_users}</div>
    },
    {
      name: "Total Cost (Rs.)",
      selector: (row) => row.course_price,
      sortable: true,
      cell: row => <div className="price-cell">{row.course_price.toLocaleString()}</div>
    },
    {
      name: "Transaction Status",
      selector: (row) => row.status,
      sortable: true,
      cell: row => {
        const status = row.status || "Pending";
        return <div className={`status-badge ${status.toLowerCase()}`}>{status}</div>;
      }
    },
    {
      name: "File Name",
      selector: (row) => row.file_name,
      sortable: true,
      cell: row => <div className="filename-cell">{row.file_name}</div>
    }, {
      name: "View",
      cell: row => (
        <div 
          className="view-btn"
          onClick={() => navigate(`/pay-group-list/${row.transaction_id}`)}
          title="View Details"
        >
          <FaEye size={20} style={{color:"#92a8fdff",textAlign:"center",cursor:"pointer"}}/> {/* icon */}
        </div>
      ),
      width: "80px"
    }
    // { 
    //   name: "Link", 
    //   cell: row => (
    //     <button 
    //       className="download-link"
    //       onClick={() => handleDownload(row.file_name)}
    //       title="Download file"
    //     >
    //       Download
    //     </button>
    //   )
    // },
    // { 
    //   name: "Invoice", 
    //   cell: row => (
    //     <button 
    //       className="invoice-btn"
    //       onClick={() => generateInvoice(row)}
    //       title="Generate invoice"
    //     >
    //       Invoice
    //     </button>
    //   )
    // },
  ];

  // Safe filtering that handles cases where data might be malformed
  const filteredData = tableData.filter(item => {
    try {
      return (
        (item.transaction_id && item.transaction_id.toLowerCase().includes(filterText.toLowerCase())) ||
        (item.date && item.date.toLowerCase().includes(filterText.toLowerCase())) ||
        (item.status && item.status.toLowerCase().includes(filterText.toLowerCase())) ||
        (item.file_name && item.file_name.toLowerCase().includes(filterText.toLowerCase()))
      );
    } catch (e) {
      console.error("Error filtering data:", e);
      return false;
    }
  });

  const handleDownload = (filename) => {
    // Implement your file download logic here
    // console.log("Downloading:", filename);
    alert(`Download functionality for ${filename} would be implemented here`);
  };

  const generateInvoice = (transaction) => {
    // Implement invoice generation logic here
    // console.log("Generating invoice for:", transaction);
    alert(`Invoice generation for transaction ${transaction.transaction_id} would be implemented here`);
  };

  const exportToExcel = () => {
    try {
      // Prepare data for export (exclude internal fields like serialNo)
      const exportData = filteredData.map(({ serialNo, ...rest }) => rest);

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Payment History");
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "payment_history.xlsx");
    } catch (e) {
      console.error("Error exporting to Excel:", e);
      alert("Failed to export to Excel. Please try again.");
    }
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF();
      doc.text("Payment History", 14, 10);

      const tableColumn = ["Transaction ID", "Date", "No of Students", "Total Cost", "Status", "File Name"];
      const tableRows = filteredData.map((transaction) => [
        transaction.transaction_id,
        new Date(transaction.date).toLocaleDateString(),
        transaction.no_of_users,
        `Rs. ${transaction.course_price.toLocaleString()}`,
        transaction.status,
        transaction.file_name,
      ]);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 20,
      });

      doc.save("payment_history.pdf");
    } catch (e) {
      console.error("Error exporting to PDF:", e);
      alert("Failed to export to PDF. Please try again.");
    }
  };

  return (
    <>
      <Navbar />
      <TitleBar title={title} image={image} />
      <div className="user-table-container">
        <div className="user-table-header">
          <div className="user-table-controls">
            <div className="user-table-search">
              <input
                type="text"
                placeholder="Search by transaction ID, date, status or file name..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="user-search-input"
              />
            </div>

            <div className="user-table-export">
              <button onClick={exportToExcel} className="export-btn export-excel">
                <span> <FileUploadIcon />Export to Excel</span>
              </button>
              <button onClick={exportToPDF} className="export-btn export-pdf">
                <span> <FileUploadIcon /> Export to PDF</span>
              </button>
            </div>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          highlightOnHover
          striped
          progressPending={loading}
          noDataComponent={
            <div className="no-data-message">
              {loading ? "Loading payment history..." : "No payment history data available"}
            </div>
          }
          customStyles={{
            head: {
              style: {
                fontSize: '15px',
              },
            },
            headRow: {
              style: {
                backgroundColor: '#ABBAF6',
                borderTop: '1px solid #e0e0e0',
                borderBottom: '1px solid #e0e0e0',
              },
            },
            headCells: {
              style: {
                fontWeight: '600',
                color: '#2c3e50',
                paddingLeft: '12px',
                paddingRight: '12px',
              },
            },
            cells: {
              style: {
                paddingLeft: '12px',
                paddingRight: '12px',
              },
            },
            rows: {
              style: {
                fontSize: '14px',
                '&:hover': {
                  backgroundColor: '#f8f9fa',
                },
              },
              stripedStyle: {
                backgroundColor: '#f8fafc',
              }
            },
            pagination: {
              style: {
                borderTop: 'none',
              },
            },
          }}
        />
      </div>
      <Footer />
    </>
  );
};

PaymentHistory.propTypes = {
  title: PropTypes.string,
  image: PropTypes.string,
};

PaymentHistory.defaultProps = {
  title: "Payment History",
};

export default PaymentHistory;